/**
 * Transformers between the three vehicle shapes:
 *  - InventoryVehicle (a `getVehicleInventoryV2` row)
 *  - VehicleFormValues (flat antd Form state inside the modal)
 *  - Vehicle (an item in `state.vehiclesOfInterest`, sent to the mutation)
 *
 * IMPORTANT: `VehicleOfInterestInputObject` (WebApp-API/api/schema.py) is a
 * STRICT GraphQL input. Sending any field it doesn't declare makes the whole
 * create/update mutation fail validation. We therefore keep rich display-only
 * fields on the in-memory Vehicle item but whitelist down to the supported
 * subset with {@link toMutationInput} right before the mutation runs.
 * See BACKEND_GAPS.md.
 *
 * @module vehicleModal/utils/vehiclePayload
 */

/**
 * `input_type` values the API resolvers match on — **case-sensitive, lowercase**.
 * `createLeadOpportunity` / `updateLeadOpportunity` compare against `"vin"` /
 * `"inventory"` / `"ymm"`; `createLeadOpportunity` rejects anything else outright
 * with "Invalid input type for vehicle!". Mirrors `VEHICLE_INPUT_TYPE` in the
 * mobile app.
 */
export const VEHICLE_INPUT_TYPE = {
  VIN: "vin",
  INVENTORY: "inventory",
  YMM: "ymm",
};

/**
 * Keys accepted by `VehicleOfInterestInputObject` (camelCase as the GraphQL
 * schema exposes them). Anything outside this list is display-only.
 */
export const MUTATION_VEHICLE_KEYS = [
  "id",
  "leadId",
  "inputType",
  "make",
  "model",
  "year",
  "text",
  "trim",
  "stockNumber",
  "vin",
  "description",
  "budget",
  "isCurrent",
  "customerInterest",
  "isPrimary",
  "interiorColor",
  "exteriorColor",
  "inventoryId",
  "isDeleted",
  "vehicleInventoryId",
  // Columns added for the new vehicle form.
  "bodyStyle",
  "condition",
  "mileage",
  // Catch-all JSON for fields without a dedicated column (sent as a JSON string).
  "extraData",
];

/**
 * Display-only fields the backend stores in the `extra_data` JSONB column
 * (no dedicated table column). Keys are camelCase and round-trip via extraData.
 */
export const EXTRA_DATA_KEYS = [
  // NOTE: `isCertified` is intentionally excluded — it is not part of the
  // vehicle-of-interest object, so it is display/form-only and never sent.
  "fuelType",
  "transmission",
  "modelNumber",
  "driveType",
  "vehicleType",
  "engineCylinders",
  "doors",
  "seats",
  "driveTrain",
  "msrp",
  "priceRangeMin",
  "priceRangeMax",
  "mileageRangeMin",
  "mileageRangeMax",
  // Sell/Trade preferences (no dedicated column).
  "odometer",
  "titleType",
  "expectedPrice",
  "payoffAmount",
  // Service preferences (no dedicated column).
  "plate",
  "unitNumber",
  "factoryWarrantyMonths",
  "factoryWarrantyMiles",
  "extendedWarrantyMonths",
  "extendedWarrantyMiles",
];

/**
 * Collect the extra (non-column) vehicle fields into a plain object for the
 * `extra_data` JSONB column. Empty/undefined values are skipped.
 * @param {import("../types").Vehicle} vehicle
 * @returns {Object}
 */
export function buildExtraData(vehicle = {}) {
  /** @type {Record<string, *>} */
  const out = {};
  EXTRA_DATA_KEYS.forEach(key => {
    const value = vehicle[key];
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  });
  return out;
}

const toStr = v => (v === null || v === undefined || v === "" ? undefined : String(v));

/**
 * Parse the `extra_data` returned by the server (a JSON string or already an
 * object) back into a plain object.
 * @param {string|Object} raw
 * @returns {Object}
 */
export function parseExtraData(raw) {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
}

/**
 * Build a Vehicle item from a selected inventory row.
 * Backend-persisted fields are set directly; the richer specs ride along as
 * display-only fields so the card + edit view can show them this session.
 *
 * @param {import("../types").InventoryVehicle} inv
 * @param {import("../types").CustomerInterest} customerInterest
 * @returns {import("../types").Vehicle}
 */
export function inventoryToVehicle(inv, customerInterest) {
  if (!inv) return null;
  return {
    inputType: "inventory",
    customerInterest,
    inventoryId: inv.id,
    year: toStr(inv.year),
    make: inv.make || undefined,
    model: inv.model || undefined,
    trim: inv.trim || undefined,
    vin: inv.vin || undefined,
    stockNumber: inv.stockNumber || undefined,
    exteriorColor: inv.exteriorColor || undefined,
    interiorColor: inv.interiorColor || undefined,
    // ---- display-only (not persisted, see BACKEND_GAPS.md) ----
    condition: inv.isNew ? "new" : "used",
    isCertified: Boolean(inv.isCertified),
    bodyStyle: inv.bodyStyle || undefined,
    fuelType: inv.fuelType || undefined,
    transmission: inv.transmission || undefined,
    modelNumber: inv.modelNumber || undefined,
    driveType: inv.driveType || undefined,
    vehicleType: inv.vehicleType || undefined,
    engineCylinders: toStr(inv.engineCylinders),
    doors: toStr(inv.doors),
    seats: toStr(inv.seats),
    driveTrain: inv.driveTrain || undefined,
    mileage: toStr(inv.mileage),
    msrp: toStr(inv.msrp ?? inv.sellingPrice),
    media: inv.vehicleInventoryMedias || [],
  };
}

/**
 * Convert flat modal form values into a Vehicle item, preserving identity
 * fields (id/inventoryId/isPrimary) from any vehicle being edited.
 *
 * @param {import("../types").VehicleFormValues} values
 * @param {Object} opts
 * @param {import("../types").CustomerInterest} opts.customerInterest
 * @param {import("../types").VehicleInputType} opts.inputType
 * @param {import("../types").Vehicle} [opts.existing] - when editing
 * @returns {import("../types").Vehicle}
 */
export function formValuesToVehicle(
  values = {},
  { customerInterest, inputType, existing = {} } = {}
) {
  return {
    ...existing,
    inputType,
    customerInterest,
    // identity
    id: existing.id,
    inventoryId: existing.inventoryId,
    // persisted
    year: toStr(values.year),
    make: values.make || undefined,
    model: values.model || undefined,
    trim: values.trim || undefined,
    vin: values.vin ? values.vin.trim().toUpperCase() : undefined,
    stockNumber: values.stockNumber || undefined,
    exteriorColor: values.exteriorColor || undefined,
    interiorColor: values.interiorColor || undefined,
    // The single persisted `budget` field takes Expected Price (sell-side) or
    // Price Range min (buy), whichever the rendered form provided.
    budget: toStr(values.expectedPrice != null ? values.expectedPrice : values.priceRangeMin),
    // Additional notes map to the persisted `description` field.
    description: values.additionalNotes || undefined,
    isPrimary: Boolean(values.isPrimary),
    isCurrent: customerInterest === "TRADE" || customerInterest === "SELL",
    // `mileage` + `condition` have dedicated columns.
    mileage: toStr(values.mileage),
    // ---- display-only (not persisted) ----
    condition: values.condition,
    // Sell/Trade preferences (round-trip via extra_data).
    odometer: toStr(values.odometer),
    titleType: values.titleType || undefined,
    expectedPrice: values.expectedPrice,
    payoffAmount: values.payoffAmount,
    // Service preferences (round-trip via extra_data).
    plate: values.plate || undefined,
    unitNumber: values.unitNumber || undefined,
    factoryWarrantyMonths: values.factoryWarrantyMonths,
    factoryWarrantyMiles: values.factoryWarrantyMiles,
    extendedWarrantyMonths: values.extendedWarrantyMonths,
    extendedWarrantyMiles: values.extendedWarrantyMiles,
    isCertified: Boolean(values.isCertified),
    bodyStyle: values.bodyStyle || undefined,
    fuelType: values.fuelType || undefined,
    transmission: values.transmission || undefined,
    modelNumber: values.modelNumber || undefined,
    driveType: values.driveType || undefined,
    vehicleType: values.vehicleType || undefined,
    engineCylinders: toStr(values.engineCylinders),
    doors: toStr(values.doors),
    seats: toStr(values.seats),
    driveTrain: values.driveTrain || undefined,
    msrp: toStr(values.msrp),
    priceRangeMin: values.priceRangeMin,
    priceRangeMax: values.priceRangeMax,
    mileageRangeMin: values.mileageRangeMin,
    mileageRangeMax: values.mileageRangeMax,
    additionalNotes: values.additionalNotes || undefined,
  };
}

/**
 * Flatten a stored Vehicle item back into form values for the Edit flow.
 * @param {import("../types").Vehicle} vehicle
 * @returns {import("../types").VehicleFormValues}
 */
export function vehicleToFormValues(vehicle = {}) {
  // Server-loaded vehicles carry the non-column fields inside extra_data;
  // merge them so the form repopulates (top-level values win).
  const vehicleData = { ...parseExtraData(vehicle.extraData), ...vehicle };
  vehicle = vehicleData;
  return {
    condition: vehicle.condition || (vehicle.isNew ? "new" : undefined),
    isCertified: Boolean(vehicle.isCertified),
    isPrimary: Boolean(vehicle.isPrimary),
    vin: vehicle.vin,
    year: toStr(vehicle.year),
    make: vehicle.make,
    model: vehicle.model,
    trim: vehicle.trim,
    exteriorColor: vehicle.exteriorColor,
    interiorColor: vehicle.interiorColor,
    bodyStyle: vehicle.bodyStyle,
    fuelType: vehicle.fuelType,
    transmission: vehicle.transmission,
    stockNumber: vehicle.stockNumber,
    modelNumber: vehicle.modelNumber,
    driveType: vehicle.driveType,
    vehicleType: vehicle.vehicleType,
    engineCylinders: toStr(vehicle.engineCylinders),
    doors: toStr(vehicle.doors),
    seats: toStr(vehicle.seats),
    driveTrain: vehicle.driveTrain,
    msrp: vehicle.msrp != null ? Number(vehicle.msrp) : undefined,
    // Prefer explicit range fields; fall back to the persisted budget.
    priceRangeMin:
      vehicle.priceRangeMin != null
        ? vehicle.priceRangeMin
        : vehicle.budget != null && vehicle.budget !== ""
        ? Number(vehicle.budget)
        : undefined,
    priceRangeMax: vehicle.priceRangeMax,
    mileageRangeMin: vehicle.mileageRangeMin,
    mileageRangeMax: vehicle.mileageRangeMax,
    additionalNotes: vehicle.additionalNotes || vehicle.description,
    // Sell/Trade preferences.
    odometer: toStr(vehicle.odometer),
    titleType: vehicle.titleType,
    // Service preferences.
    plate: vehicle.plate,
    unitNumber: vehicle.unitNumber,
    factoryWarrantyMonths: vehicle.factoryWarrantyMonths,
    factoryWarrantyMiles: vehicle.factoryWarrantyMiles,
    extendedWarrantyMonths: vehicle.extendedWarrantyMonths,
    extendedWarrantyMiles: vehicle.extendedWarrantyMiles,
    mileage: vehicle.mileage != null ? Number(vehicle.mileage) : undefined,
    expectedPrice:
      vehicle.expectedPrice != null
        ? vehicle.expectedPrice
        : vehicle.budget != null && vehicle.budget !== ""
        ? Number(vehicle.budget)
        : undefined,
    payoffAmount: vehicle.payoffAmount,
  };
}

/**
 * Whitelist a Vehicle item down to the keys the mutation accepts, dropping
 * undefined values. This is what guards against GraphQL input validation
 * errors from the display-only fields.
 *
 * @param {import("../types").Vehicle} vehicle
 * @returns {Object}
 */
export function toMutationInput(vehicle = {}) {
  /** @type {Record<string, *>} */
  const out = {};
  MUTATION_VEHICLE_KEYS.forEach(key => {
    if (key === "extraData") return; // packed separately below
    let value = vehicle[key];
    if (value === undefined || value === null || value === "") return;
    // `mileage` is an Int column server-side.
    if (key === "mileage") {
      value = Number(value);
      if (Number.isNaN(value)) return;
    }
    out[key] = value;
  });
  // A vehicle with no `inputType` fails `createLeadOpportunity` outright, so fall
  // back to the manual type — the only branch that persists the fields as sent.
  // Mirrors the mobile app's `toMutationInput`.
  if (!out.inputType) out.inputType = VEHICLE_INPUT_TYPE.YMM;
  // Fields without a dedicated column are sent as a JSON string (the GraphQL
  // JSONString scalar). Backend stores them on extra_data (JSONB).
  const extra = buildExtraData(vehicle);
  if (Object.keys(extra).length) out.extraData = JSON.stringify(extra);
  return out;
}

/**
 * Sanitize the whole `vehiclesOfInterest` list for the create/update mutation.
 * @param {import("../types").Vehicle[]} list
 * @returns {Object[]}
 */
export function sanitizeVehiclesForMutation(list = []) {
  return (list || []).map(toMutationInput);
}
